import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Award, Image as ImageIcon } from 'lucide-react';
import { Button, Input, TextArea, SearchSelect } from '../shared';
import { useVendorSpotlightForm } from '../../hooks/useVendorSpotlightForm';

const CreateVendorSpotlightModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  spotlight = null, 
  isEdit = false, 
  isLoading = false 
}) => {
  const [errors, setErrors] = useState({});

  // Use custom hook for form data and vendor fetching
  const {
    formData,
    setFormData,
    selectedVendorId,
    setSelectedVendorId,
    vendorOptions,
    isLoadingVendors,
    vendorsResponse,
  } = useVendorSpotlightForm({ isOpen, isEdit, spotlight });
  
  // Debug logging
  useEffect(() => {
    // if (isOpen && !isEdit) {
    //   console.log('Vendors Response:', vendorsResponse);
    //   console.log('Vendor Options:', vendorOptions);
    //   console.log('Loading Vendors:', isLoadingVendors);
    // }
  }, [isOpen, isEdit, vendorsResponse, vendorOptions, isLoadingVendors]);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      setFormData(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (formData.rating && (formData.rating < 0 || formData.rating > 5)) {
      newErrors.rating = 'Rating must be between 0 and 5';
    }

    if (formData.review_count && formData.review_count < 0) {
      newErrors.review_count = 'Review count must be a positive number';
    }

    if (formData.completed_events && formData.completed_events < 0) {
      newErrors.completed_events = 'Completed events must be a positive number';
    }

    if (formData.years_in_business && formData.years_in_business < 0) {
      newErrors.years_in_business = 'Years in business must be a positive number';
    }

    if (formData.starting_price && formData.starting_price < 0) {
      newErrors.starting_price = 'Starting price must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Filter out empty features
    const cleanedFeatures = formData.features.filter(f => f.trim() !== '');

    const submitData = {
      ...formData,
      features: cleanedFeatures.length > 0 ? cleanedFeatures : [],
      rating: formData.rating ? parseFloat(formData.rating) : 0,
      review_count: formData.review_count ? parseInt(formData.review_count) : 0,
      completed_events: formData.completed_events ? parseInt(formData.completed_events) : 0,
      years_in_business: formData.years_in_business ? parseInt(formData.years_in_business) : 0,
      starting_price: formData.starting_price ? parseFloat(formData.starting_price) : null,
      sort_order: parseInt(formData.sort_order) || 0,
      vendor_id: formData.vendor_id || null,
    };

    onSave(submitData);
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative my-2 sm:my-4">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center min-w-0 flex-1">
            <div className="flex-shrink-0 h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center">
              <Award className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
            </div>
            <h3 className="ml-2 sm:ml-3 text-sm sm:text-base md:text-lg font-medium text-gray-900 truncate">
              {isEdit ? 'Edit Vendor Spotlight' : 'Create Vendor Spotlight'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          {/* Vendor Selection - Only show when creating, not editing */}
          {!isEdit && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-2 sm:mb-3">
                Select Vendor to Auto-Populate Fields
              </h4>
              <SearchSelect
                label="Search Active Vendors"
                value={selectedVendorId}
                onChange={(value) => {
                  setSelectedVendorId(value);
                  // Clear form errors when selecting vendor
                  setErrors({});
                }}
                options={vendorOptions}
                placeholder="Search and select a vendor..."
                searchPlaceholder="Type to search vendors..."
                clearable={true}
                searchable={true}
                className="w-full"
              />
              {selectedVendorId && (
                <p className="mt-2 text-xs text-gray-600">
                  ✓ Vendor selected. Form fields will be auto-populated with vendor data.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Basic Information */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <Input
                  label="Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Vendor name"
                  error={errors.name}
                  required
                />
              </div>

              <div>
                <Input
                  label="Category *"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="e.g., Photo Booth & Entertainment"
                  error={errors.category}
                  required
                />
              </div>

              <div>
                <TextArea
                  label="Description *"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Vendor description"
                  rows={4}
                  error={errors.description}
                  required
                />
              </div>

              <div>
                <Input
                  label="Location *"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Accra, Greater Accra"
                  error={errors.location}
                  required
                />
              </div>
            </div>

            {/* Images & Media */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">Images & Media</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Image URL
                </label>
                <div className="space-y-2">
                  <Input
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    type="url"
                  />
                  {formData.image && (
                    <div className="relative inline-block w-full">
                      <div className="relative w-full max-w-xs">
                        <img
                          src={formData.image}
                          alt="Main preview"
                          className="w-full h-32 sm:h-40 md:h-48 object-cover rounded-lg border-2 border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden w-full h-32 sm:h-40 md:h-48 bg-gray-100 rounded-lg border-2 border-gray-200 items-center justify-center flex-col">
                          <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mb-2" />
                          <p className="text-xs sm:text-sm text-gray-500">Failed to load image</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          title="Remove image"
                        >
                          <X className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {!formData.image && (
                    <div className="w-full max-w-xs h-32 sm:h-40 md:h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                      <div className="text-center">
                        <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs sm:text-sm text-gray-500">No image preview</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Image URL
                </label>
                <div className="space-y-2">
                  <Input
                    name="profile_image"
                    value={formData.profile_image}
                    onChange={handleChange}
                    placeholder="https://example.com/profile.jpg"
                    type="url"
                  />
                  {formData.profile_image && (
                    <div className="relative inline-block">
                      <div className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32">
                        <img
                          src={formData.profile_image}
                          alt="Profile preview"
                          className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full object-cover border-2 sm:border-4 border-gray-200 shadow-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 bg-gray-100 rounded-full border-2 sm:border-4 border-gray-200 items-center justify-center flex-col">
                          <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, profile_image: '' }))}
                          className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          title="Remove image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                  {!formData.profile_image && (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 bg-gray-100 rounded-full border-2 sm:border-4 border-dashed border-gray-300 flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stats & Ratings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">Statistics</h3>
              
              <div>
                <Input
                  label="Rating (0-5)"
                  name="rating"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={formData.rating}
                  onChange={handleChange}
                  placeholder="4.96"
                  error={errors.rating}
                />
              </div>

              <div>
                <Input
                  label="Review Count"
                  name="review_count"
                  type="number"
                  min="0"
                  value={formData.review_count}
                  onChange={handleChange}
                  placeholder="324"
                  error={errors.review_count}
                />
              </div>

              <div>
                <Input
                  label="Completed Events"
                  name="completed_events"
                  type="number"
                  min="0"
                  value={formData.completed_events}
                  onChange={handleChange}
                  placeholder="500"
                  error={errors.completed_events}
                />
              </div>

              <div>
                <Input
                  label="Years in Business"
                  name="years_in_business"
                  type="number"
                  min="0"
                  value={formData.years_in_business}
                  onChange={handleChange}
                  placeholder="5"
                  error={errors.years_in_business}
                />
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">Pricing & Details</h3>
              
              <div>
                <Input
                  label="Starting Price"
                  name="starting_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.starting_price}
                  onChange={handleChange}
                  placeholder="450"
                  error={errors.starting_price}
                />
              </div>

              <div>
                <Input
                  label="Badge"
                  name="badge"
                  value={formData.badge}
                  onChange={handleChange}
                  placeholder="e.g., Top Rated Pro"
                />
              </div>

              <div>
                <Input
                  label="Vendor ID (Optional)"
                  name="vendor_id"
                  value={formData.vendor_id}
                  onChange={handleChange}
                  placeholder="Link to actual vendor"
                />
              </div>

              <div>
                <Input
                  label="Sort Order"
                  name="sort_order"
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Features</h3>
            <div className="space-y-2">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    placeholder={`Feature ${index + 1}`}
                    className="flex-1"
                  />
                  {formData.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Feature
              </button>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
              Active
            </label>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex items-center justify-center w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEdit ? 'Update Spotlight' : 'Create Spotlight'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreateVendorSpotlightModal;

